import { useEffect, useRef } from 'react';

/**
 * Persist a list-page's state across route navigation so tapping an item,
 * viewing its detail page, and tapping back returns the user to the SAME
 * list instantly — no spinner, no empty-state flash.
 *
 * Scroll handling philosophy:
 *   We do NOT try to restore the user's exact scrollY. That fights the
 *   browser's built-in scroll restoration and produces a brief "wrong
 *   position" flash (footer momentarily visible, then jumps).
 *   Instead, we disable the browser's auto-restore and let the caller scroll
 *   to a known anchor inside `onRestore` via `scrollIntoView` — always lands
 *   at the same predictable spot, no flash possible.
 *
 * Storage:
 *   `<key>:state` → JSON snapshot of the caller-supplied state object,
 *   sessionStorage (cleared when browser closes, per-tab).
 *
 * Usage:
 *   const ref = useRef(null);
 *   useListStatePersistence({
 *     key: 'findExpats',
 *     state: { results, hasSearched, ... },
 *     enabled: results.length > 0,
 *     onRestore: (cached) => {
 *       setResults(cached.results); ...;
 *       // Scroll to the list header instantly (no animation, no flash):
 *       ref.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
 *     },
 *   });
 *   ...
 *   <h1 ref={ref}>50 Expats Found</h1>
 */
export function useListStatePersistence({
  key,
  state,
  onRestore,
  enabled = true,
  freshnessMs = 10 * 60 * 1000,
}) {
  const stateKey = `${key}:state`;
  const restoredRef = useRef(false);

  // Mount-time: switch off browser scroll restoration for this navigation,
  // then restore cached state if fresh. onRestore is responsible for
  // positioning the scroll via scrollIntoView on a known anchor.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    // Tell the browser not to auto-restore scroll on this history entry —
    // prevents the "footer flash" caused by browsers restoring a position
    // based on the OLD page height before our list has remounted.
    const prevScrollRestoration = window.history.scrollRestoration;
    try { window.history.scrollRestoration = 'manual'; } catch { /* noop */ }

    let cached = null;
    try {
      const raw = sessionStorage.getItem(stateKey);
      if (raw) cached = JSON.parse(raw);
    } catch { /* ignore malformed cache */ }

    const fresh = cached && (Date.now() - (cached.__ts || 0)) < freshnessMs;
    if (fresh && typeof onRestore === 'function') {
      try { onRestore(cached); } catch { /* swallow restore errors */ }
    } else if (!fresh) {
      try { sessionStorage.removeItem(stateKey); } catch { /* noop */ }
    }

    // Restore the previous scrollRestoration mode when this component
    // unmounts, so other pages don't lose browser-default scroll behavior.
    return () => {
      try { window.history.scrollRestoration = prevScrollRestoration || 'auto'; } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Snapshot caller's state whenever it changes and is non-empty.
  useEffect(() => {
    if (!enabled) return;
    try {
      sessionStorage.setItem(stateKey, JSON.stringify({ ...state, __ts: Date.now() }));
    } catch { /* sessionStorage quota or unavailable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(state)]);

  return {
    /** Manually drop cache — e.g. when the user clicks "Reset" / "Clear search". */
    clear: () => {
      try { sessionStorage.removeItem(stateKey); } catch { /* noop */ }
    },
    /** Deprecated no-op kept for compatibility with old callsites that captured scroll. */
    markLeaving: () => {},
  };
}
